import type { AppDefinition } from '@novasign/app-common'
import { ContentEntity, ScreenEntity, SubscriptionEntity, UserEntity } from '../src/entity'
import { LayoutStyle } from '../src/types/layout.types'
import {
  CanvasData,
  CanvasObject,
  CanvasObjectType,
  AspectRatio,
  TextObject,
  RectObject,
  CircleObject,
  GradientColorStop,
  GradientDirection,
  LinearGradientFill,
  FillValue,
  CanvasLayoutInput,
} from '../src/types/canvas-layout.types'

export { CanvasObjectType, AspectRatio }
export type {
  CanvasData,
  CanvasObject,
  TextObject,
  RectObject,
  CircleObject,
  GradientColorStop,
  GradientDirection,
  LinearGradientFill,
  FillValue,
  CanvasLayoutInput,
}

export enum SubscriptionPlanType {
  BASIC = 'Basic',
  PREMIUM = 'Premium',
}

export interface ContentInput {
  id?: string
  name: string
  assetId: string
  publicId: string
  type: string
  format: string
  url: string
  thumbnailUrl: string
  duration?: number
  metadata: Record<string, any>
  folderId?: string | null
}

/**
 * Per-quality delivery URLs for a Cloudflare image, built server-side.
 * Partial so a client can add a quality tier without every producer being updated first;
 * callers fall back to the canonical `url` on a miss (see resolveImageUrl).
 */
export type ImageUrls = Partial<Record<ImageQuality, string>>

export type MediaContent = Pick<
  ContentEntity,
  'id' | 'name' | 'format' | 'url' | 'thumbnailUrl' | 'metadata' | 'publicId' | 'externalId' | 'storageType'
> & { type: 'image' | 'video'; duration?: number; folderId?: string | null; urls?: ImageUrls }

export interface MediaFolder {
  id: string
  name: string
  parentId: string | null
  createdDate: Date
  updatedDate: Date
  mediaCount?: number
}

export type AppContent = Pick<ContentEntity, 'id' | 'name' | 'metadata' | 'format'> & {
  type: 'app'
  duration?: number
}

export type Content = MediaContent | AppContent

export interface PlaylistItem {
  duration: number
  order?: number
  content?: Content
  canvasLayout?: CanvasLayout
}

export interface Playlist {
  id: string
  name: string
  items: PlaylistItem[]
}

export type PlaylistInput = Omit<Playlist, 'id'> & { id?: string }

export const SCHEDULE_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export interface Schedule {
  schedules: Array<{
    id: string
    weekly: {
      [key: string]: {
        start: string
        end: string
      }
    }
  }>
}

export type ChannelItem = {
  clientId?: string
  duration: number
  order: number
  // either content, playlist, or canvasLayout should be set
  content?: Content
  playlist?: Playlist
  canvasLayout?: CanvasLayout
  schedule?: Schedule
}

export interface ChannelPane {
  id?: string
  name: string
  order?: number
  items: ChannelItem[]
  style: LayoutStyle
}

export interface Channel {
  id: string
  name: string
  panes: ChannelPane[]
  layout?: Layout
}

export type ChannelInput = Omit<Channel, 'id'> & {
  id?: string
}

export interface Playable {
  duration: number
  content?: Content
  canvasLayout?: CanvasLayout
  schedule?: Schedule
}

export type PlayableList = Playable[]

export type ScreenSource = Playlist | Channel

export interface ItemList<T> {
  items: T[]
  count: number
}

export interface AppDefinitions {
  apps: AppDefinition[]
}

// NOTE: separated playlist type because we were getting a circular depencey error from typescript compiler
/*
Type of property 'content' circularly references itself in mapped type '{ id: "id"; content: "content" | `content.${any}`; duration: "duration"; order: "order"; playlist: "playlist" | `playlist.${any}`; hasId: "hasId"; save: "save"; remove: "remove"; softRemove: "softRemove"; recover: "recover"; reload: "reload"; }'
*/
export type Screen = Pick<
  ScreenEntity,
  | 'id'
  | 'name'
  | 'paircode'
  | 'orientation'
  | 'platform'
  | 'syncronized'
  | 'timezone'
  | 'screenType'
  | 'createdDate'
  | 'localPlayback'
  | 'imageQuality'
  | 'lastSeen'
> & {
  playlist: Playlist | null
  channel: Channel | null
  canvasLayout: CanvasLayout | null
  schedule: ScheduleSummary | null
  isOnline?: boolean
}

/** Only what the player needs to go fetch the schedule itself. */
export interface ScheduleSummary {
  id: string
  name: string
}

export type ScheduleTargetType = 'playlist' | 'canvasLayout' | 'content'

export interface ScheduleTarget {
  type: ScheduleTargetType
  id: string
  name: string
  /** Embedded only for single-item targets on the player route. */
  content?: Content
}

export interface ScheduleEventDto {
  id: string
  name: string
  /** Lower wins when two events overlap; this is the list order in the editor. */
  priority: number
  color: string
  target: ScheduleTarget | null
  startDate: string
  endDate: string | null
  allDay: boolean
  startTime: string
  endTime: string
  /** RFC 5545 rule without DTSTART; null means the event happens once. */
  rrule: string | null
  /** Days this series skips, because one occurrence was edited on its own. */
  skipDates: string[]
}

export interface ScheduleDto {
  id: string
  name: string
  timezone: string | null
  fallback: ScheduleTarget | null
  events: ScheduleEventDto[]
  screenCount: number
  /** Only in the list, so it can say what each schedule plays without a request per row. */
  now?: ScheduleNow
  createdDate: string
  updatedDate: string
}

export interface ScheduleConflict {
  eventId: string
  name: string
  /** First moment the two collide, so the message can name a day. */
  at: string
}

export interface ScheduleConflictCheck {
  conflicts: ScheduleConflict[]
  /** Which side the specificity rule favours, offered as the default choice. */
  suggestWinner: 'candidate' | 'existing'
  /** Pass back as placeAbove to make the new event win over every conflict. */
  placeAbove: string | null
  /** Pass back as placeBelow to let every conflicting event keep winning. */
  placeBelow: string | null
}

export interface ScheduleInput {
  name?: string
  timezone?: string | null
  fallbackPlaylistId?: string | null
  fallbackCanvasLayoutId?: string | null
  fallbackContentId?: string | null
}

export interface ScheduleEventInput {
  name?: string
  color?: string
  playlistId?: string | null
  canvasLayoutId?: string | null
  contentId?: string | null
  startDate?: string
  endDate?: string | null
  allDay?: boolean
  startTime?: string
  endTime?: string
  rrule?: string | null
  skipDates?: string[] | null
  /** Put this event straight above that one, so it wins where they overlap. */
  placeAbove?: string | null
  /** Put this event straight below that one, so the other keeps winning. */
  placeBelow?: string | null
}

export type TimezoneSource = 'screen' | 'device' | 'schedule' | 'fallback'

export interface ScheduleNow {
  timezone: string
  timezoneSource: TimezoneSource
  event: { id: string; name: string; target: ScheduleTarget | null } | null
  /** ISO instant the running event ends at. */
  until: string | null
  fallback: ScheduleTarget | null
  nextChangeAt: string | null
}

export interface ScheduleOccurrence {
  eventId: string
  name: string
  color: string
  priority: number
  allDay: boolean
  repeats: boolean
  target: ScheduleTarget | null
  start: string
  end: string
  /** The calendar day in the schedule's timezone, used when editing a single occurrence. */
  date: string
  /** A higher event covers the whole window, so this occurrence never plays. */
  blocked: boolean
  /** The parts of the window a higher event covers. Outside these, this occurrence plays. */
  hiddenRanges: Array<{ start: string; end: string }>
}

export type ScreenInput = Pick<
  Screen,
  'id' | 'name' | 'orientation' | 'syncronized' | 'timezone' | 'localPlayback' | 'imageQuality'
>

export interface SessionData {
  token: string
  username: string
  // TODO: introduce expiresAt and refreshToken
}

export interface UserCapabilities {
  userId: string
  premiumScreensAllowed: number
  basicScreensAllowed: number
  screensAllowed: number
  storage: number // in MB
  uploadSize: number // in MB
}

// AuthUser used in FE
export interface AuthUser {
  username: string
  subscriptionType: SubscriptionPlanType.BASIC | SubscriptionPlanType.PREMIUM
  iat: number
  exp: number
  capabilities: UserCapabilities
  stripeCustomerId?: string | null
  isAdmin?: boolean
}

export const EXPERIMENTAL_FEATURES = ['localPlayback'] as const

export type ExperimentalFeature = (typeof EXPERIMENTAL_FEATURES)[number]

export type ExperimentalFeatures = Record<ExperimentalFeature, boolean>

export interface UserSettings {
  experimentalFeatures: ExperimentalFeatures
}

/** What an account holds, so the delete dialog can name it before anything is destroyed. */
export interface AccountDeletionSummary {
  screens: number
  playlists: number
  channels: number
  mediaFiles: number
}

export type AccountDeletionBlocker = 'active_subscription' | 'admin_account'

export interface AccountDeletionEligibility {
  canDelete: boolean
  /**
   * Why the account cannot go yet. 'active_subscription' is on the owner to resolve by cancelling
   * the plan; 'admin_account' is not — staff accounts are removed by another admin or in the
   * database, never by the product, so the whole install cannot lose its last administrator.
   */
  blockedBy?: AccountDeletionBlocker
  summary: AccountDeletionSummary
}

export const BASIC_SUBSCRIPTION_MAX_SCREENS = 1

export type ActiveUser = UserEntity & { capabilities: UserCapabilities }

export interface RegisterResponse {
  token: string
}

export interface DirectUploadMedia {
  id: string
  url: string
}

export enum StorageType {
  LOCAL = 'local',
  CLOUDFLARE = 'cloudflare',
  CLOUDINARY = 'cloudinary',
}

export type MediaType = 'video' | 'image'

export interface CheckoutSessionInput {
  subscription: string
  quantity?: number
}

export const orientationOptions = [
  { value: '0', label: 'Landscape' },
  { value: '90', label: 'Portrait' },
  { value: '180', label: 'Reverse landspace' },
  { value: '270', label: 'Reverse portrait' },
]

export interface SignageHwInfo {
  os: 'android' | 'ios' | 'web' | 'windows' | 'macos' | 'linux' | 'webos' | 'tizen'
  version?: string
  appVersion?: string
  isTV?: boolean
  isTesting?: boolean
  brand?: string
  model?: string
  release?: string
  diskInfo?: DiskInfo
}

export interface DiskInfo {
  availableBytes: number
  totalBytes: number
}

export interface SignageNativeConfig {
  hwInfo: SignageHwInfo
}

// Copied from UAParser.IResult type https://github.com/faisalman/ua-parser-js/blob/2.0.6/src/main/ua-parser.d.ts#L43
export interface BrowserInfo {
  ua: string
  browser: {
    name?: string
    version?: string
    major?: string
    type?: 'crawler' | 'cli' | 'email' | 'fetcher' | 'inapp' | 'mediaplayer' | 'library'
  }
  cpu: {
    architecture?: string
  }
  device: {
    type?: 'mobile' | 'tablet' | 'console' | 'smarttv' | 'wearable' | 'xr' | 'embedded' | 'desktop'
    vendor?: string
    model?: string
  }
  engine: {
    name?: string
    version?: string
  }
  os: { name?: string; version?: string }
}

export interface SignageDeviceInfo {
  hwInfo: SignageHwInfo
  browserInfo: BrowserInfo
  timezone: string
  canPlayUnmutedMedia?: boolean
  screenResolution?: { width: number; height: number }
  touchSupported?: boolean
  ip?: string // added via backend during screen registration
}

export interface Layout {
  id: string
  name: string
  description: string
  panes: LayoutPane[]
}

export interface LayoutPane {
  id?: string
  name: string
  order?: number
  style: LayoutStyle
}

export interface TemplateCategory {
  id: string
  name: string
  description?: string
  order: number
}

export interface TemplateTag {
  id: string
  name: string
  color?: string
  displayName?: string
  isFeatured?: boolean
  displayOrder?: number
}

export interface CanvasLayout {
  id: string
  name: string
  /** Public URL segment used by the landing template directory; templates only. */
  slug?: string | null
  description?: string
  canvasData: CanvasData
  aspectRatio: AspectRatio | string
  customWidth?: number
  customHeight?: number
  isTemplate: boolean
  isSystemTemplate: boolean
  isPublished: boolean
  thumbnailUrl?: string
  category?: TemplateCategory
  tags?: TemplateTag[]
  usageCount?: number
  createdDate: Date
  updatedDate: Date
}

export const SUBSCRIPTION_LIMITS: Record<
  SubscriptionPlanType,
  { UPLOAD_SIZE: number; TOTAL_STORAGE_SIZE: number; MAX_VIDEO_DURATION_SECONDS: number }
> = {
  [SubscriptionPlanType.BASIC]: {
    UPLOAD_SIZE: 10,
    TOTAL_STORAGE_SIZE: 200,
    MAX_VIDEO_DURATION_SECONDS: 600,
  },
  [SubscriptionPlanType.PREMIUM]: {
    UPLOAD_SIZE: 100,
    TOTAL_STORAGE_SIZE: Infinity,
    MAX_VIDEO_DURATION_SECONDS: 1800,
  },
} as const

export function maxVideoDurationSeconds(plan?: SubscriptionPlanType): number {
  return SUBSCRIPTION_LIMITS[plan ?? SubscriptionPlanType.BASIC].MAX_VIDEO_DURATION_SECONDS
}

export function maxVideoDurationMinutes(plan?: SubscriptionPlanType): number {
  return Math.round(maxVideoDurationSeconds(plan) / 60)
}

export const RESOURCE_LIMITS = {
  MAX_CANVAS_OBJECTS: 500,
  MAX_CANVAS_VIDEOS: 10,
  MAX_CANVAS_APPS: 10,
  MAX_CANVAS_PLAYLISTS: 10,
  MAX_CHANNEL_PANES: 10,
} as const

export enum ScreenType {
  BASIC = 'basic',
  PREMIUM = 'premium',
  GATED = 'gated',
}

/**
 * Which Cloudflare Images variant the player requests for image content.
 * HD is `publichd` (1920x1080); UHD is `public4k` (3840x3840, fit=scale-down),
 * which also covers portrait screens whose long edge exceeds 1920.
 */
export enum ImageQuality {
  HD = 'hd',
  UHD = 'uhd',
}

// Ordered for display. A future 'Automatic' option (resolution-driven) is added here, and
// becomes the default for newly paired screens without disturbing explicit choices.
export const imageQualityOptions: {
  value: ImageQuality
  label: string
  description: string
  requiresPremium?: boolean
}[] = [
  { value: ImageQuality.HD, label: 'Standard', description: 'Images up to 1920px. Best for 1080p screens.' },
  {
    value: ImageQuality.UHD,
    label: 'High',
    description: 'Images up to 3840px. For 4K and tall portrait screens.',
    requiresPremium: true,
  },
]

export function isImageQualityAllowedForScreenType(quality: ImageQuality, screenType?: ScreenType): boolean {
  return quality !== ImageQuality.UHD || screenType === ScreenType.PREMIUM
}

/**
 * The quality actually served. A stored UHD choice survives a licence downgrade so it is
 * restored on re-upgrade, but delivery falls back to HD while the screen is not premium.
 */
export function effectiveImageQuality(screen: { imageQuality?: ImageQuality; screenType?: ScreenType }): ImageQuality {
  const quality = screen.imageQuality ?? ImageQuality.HD
  return isImageQualityAllowedForScreenType(quality, screen.screenType) ? quality : ImageQuality.HD
}

export enum StripEventStatus {
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export type SubscriptionPlan = Pick<SubscriptionEntity, 'name' | 'description' | 'features'> & {
  price: number | null | undefined
}

export interface SubscriptionData {
  userCapabilities: UserCapabilities
  nextMaxScreens: number | null
  cancelAt: number | null
  currentPeriodEnd: number | null
  subscription: SubscriptionPlan
}

export interface ScreenDetail {
  screenId: string
  screenName: string
  totalVideoSize: number
  maxVideoSize: number
  totalSize: number
  hasPlaylist: boolean
  playlistId: string | undefined
  hasChannel: boolean
  channelId: string | undefined
}

export interface ContentMetrics {
  totalVideoSize: number
  maxVideoSize: number
  totalSize: number
}

export interface UsageDetails {
  assignedScreenCount: number
  assignedPlaylistCount: number
  assignedVideoPlaylistCount: number
  assignedChannelCount: number
  channelCount: number
  playlistCount: number
  totalStorage: number
  screenDetails: ScreenDetail[]
}

// Response of GET /api/setup-progress — everything the Get Started checklist needs,
// so it does not have to pull the full screen/media/playlist collections to find out.
export interface SetupProgressResponse {
  hasScreen: boolean
  hasMedia: boolean
  hasPlaylist: boolean
  hasContentOnScreen: boolean
  // "Go further" tier: seeded defaults (signage-space-* apps) do not count.
  hasLayout: boolean
  hasSchedule: boolean
  hasApp: boolean
  mediaCount: number
  firstScreenName: string | null
  firstPlaylistName: string | null
}

export interface ScreenUsageCounters {
  assignedPlaylistCount: number
  assignedVideoPlaylistCount: number
  assignedChannelCount: number
  screenDetails: ScreenDetail[]
}

export interface DailyForecast {
  Headline: {
    EffectiveDate: string
    EffectiveEpochDate: number
    Severity: number
    Text: string
    Category: string
    EndDate: string
    EndEpochDate: number
    MobileLink: string
    Link: string
  }
  DailyForecasts: Array<{
    Date: string
    EpochDate: number
    Temperature: {
      Minimum: {
        Value: number
        Unit: string
        UnitType: number
      }
      Maximum: {
        Value: number
        Unit: string
        UnitType: number
      }
    }
    Day: {
      Icon: number
      IconPhrase: string
      HasPrecipitation: boolean
      PrecipitationType?: string
      PrecipitationIntensity?: string
    }
    Night: {
      Icon: number
      IconPhrase: string
      HasPrecipitation: boolean
      PrecipitationType?: string
      PrecipitationIntensity?: string
    }
    Sources: string[]
    MobileLink: string
    Link: string
  }>
}

export const ChurnEvents = {
  PREMIUM_BANNER_SHOWN: 'premium_banner_shown',
  GATED_SCREEN_SHOWN: 'gated_screen_shown',

  FILE_SIZE_LIMIT_EXCEEDED: 'file_size_limit_exceeded',

  UPGRADE_BUTTON_CLICKED: 'upgrade_button_clicked',

  OFFER_MODAL_SHOWN: 'offer_modal_shown',
  OFFER_MODAL_CLAIMED: 'offer_modal_claimed',
  OFFER_MODAL_DISMISSED: 'offer_modal_dismissed',
} as const

export const AnalyticsEvents = {
  // === SCHEDULING ===
  SCHEDULE_CREATED: 'schedule_created',
  SCHEDULE_DELETED: 'schedule_deleted',
  SCHEDULE_EVENT_CREATED: 'schedule_event_created',
  SCHEDULE_EVENT_UPDATED: 'schedule_event_updated',
  SCHEDULE_EVENT_DELETED: 'schedule_event_deleted',
  SCHEDULE_OCCURRENCE_DETACHED: 'schedule_occurrence_detached',
  SCHEDULE_FALLBACK_SET: 'schedule_fallback_set',
  SCHEDULE_ASSIGNED_TO_SCREENS: 'schedule_assigned_to_screens',
  // The pair that says whether the specificity default guesses what people mean
  SCHEDULE_CONFLICT_SHOWN: 'schedule_conflict_shown',
  SCHEDULE_CONFLICT_RESOLVED: 'schedule_conflict_resolved',
  SCHEDULE_CONFLICT_SWAPPED: 'schedule_conflict_swapped',

  // === EXISTING ===
  CHANNEL_CREATED: 'channel_created',
  CHANNEL_LAYOUT_CHANGED: 'channel_layout_changed',
  CHANNEL_SCHEDULE_CHANGED: 'channel_schedule_changed',
  LIVE_PREVIEW_OPENED: 'live_preview_opened',
  CIRCULAR_REFERENCE_BLOCKED: 'circular_reference_blocked',
  CIRCULAR_REFERENCE_DEPTH_LIMIT: 'circular_reference_depth_limit',
  LAYOUT_CREATED: 'layout_created',
  LAYOUT_CREATED_FROM_TEMPLATE: 'layout_created_from_template',
  SCREEN_CONTENT_ASSIGNED: 'screen_content_assigned',

  // === NAVIGATION ===
  SIDEBAR_ITEM_CLICKED: 'sidebar_item_clicked',
  // Fires on the What's New page itself, so visits from a direct link or the changelog modal
  // are counted too — SPA route changes send no $pageview.
  WHATSNEW_VIEWED: 'whatsnew_viewed',
  PAGE_VIEWED: 'page_viewed',
  EXTERNAL_LINK_CLICKED: 'external_link_clicked',

  // === SCREENS ===
  WEB_PLAYER_LINK_CLICKED: 'web_player_link_clicked',
  DESKTOP_PLAYER_LINK_CLICKED: 'desktop_player_link_clicked',
  SCREEN_CREATED: 'screen_created',
  SCREEN_DELETED: 'screen_deleted',
  SCREEN_PAIRED: 'screen_paired',
  SCREEN_PAIR_FAILED: 'screen_pair_failed',
  SCREEN_PAIR_INVALID_CODE: 'screen_pair_invalid_code',
  PREMIUM_UPSELL_VIEWED: 'premium_upsell_viewed',
  PAIRING_HELP_OPENED: 'pairing_help_opened',
  PAIRING_CODE_PASTED: 'pairing_code_pasted',
  SCREEN_RELOADED: 'screen_reloaded',
  SCREEN_TYPE_CHANGED: 'screen_type_changed',
  SCREEN_TYPE_CHANGE_FAILED: 'screen_type_change_failed',
  SCREEN_MANAGED: 'screen_managed',
  SCREEN_PREVIEWED: 'screen_previewed',
  SCREEN_CONTENT_CLEARED: 'screen_content_cleared',
  SCREEN_CONTENT_VIEWED: 'screen_content_viewed',

  // === PLAYLISTS ===
  PLAYLIST_CREATED: 'playlist_created',
  PLAYLIST_UPDATED: 'playlist_updated',
  PLAYLIST_DELETED: 'playlist_deleted',
  PLAYLIST_ITEM_ADDED: 'playlist_item_added',
  PLAYLIST_ITEM_REMOVED: 'playlist_item_removed',
  PLAYLIST_ITEM_REORDERED: 'playlist_item_reordered',
  PLAYLIST_SAVE_FAILED: 'playlist_save_failed',
  PLAYLIST_CAST_MODAL_OPENED: 'playlist_cast_modal_opened',
  PLAYLIST_CAST_COMPLETED: 'playlist_cast_completed',

  // === CHANNELS ===
  CHANNEL_DELETED: 'channel_deleted',
  CHANNEL_SAVE_FAILED: 'channel_save_failed',
  CHANNEL_ITEM_ADDED: 'channel_item_added',
  CHANNEL_ITEM_REMOVED: 'channel_item_removed',
  CHANNEL_ITEM_REORDERED: 'channel_item_reordered',

  // === MEDIA ===
  // MEDIA_UPLOADED fires per-file (one event per successful upload). UPLOAD_STARTED/UPLOAD_COMPLETED fire
  // per-batch (one each per modal-level upload session). UPLOAD_MODAL_* mark when the user opens/closes the
  // dashboard. Do NOT use UPLOAD_COMPLETED to count uploads — funnels would double-count vs MEDIA_UPLOADED.
  MEDIA_UPLOADED: 'media_uploaded',
  MEDIA_UPLOAD_FAILED: 'media_upload_failed',
  VIDEO_DURATION_REJECTED: 'video_duration_rejected',
  MEDIA_DELETED: 'media_deleted',
  MEDIA_BULK_DELETED: 'media_bulk_deleted',
  UPLOAD_MODAL_OPENED: 'upload_modal_opened',
  UPLOAD_MODAL_CLOSED: 'upload_modal_closed',
  UPLOAD_STARTED: 'upload_started',
  UPLOAD_COMPLETED: 'upload_completed',
  IMAGE_EDITED: 'image_edited',
  MEDIA_FILE_ADDED: 'media_file_added',

  // === GOOGLE DRIVE PICKER ===
  // Funnel: drive_tab_opened → drive_auth_* → drive_picker_shown → drive_files_picked → media_file_added
  // → media_uploaded. Every media/upload event carries `provider` ('google_drive' | 'local') so the Drive
  // path can be compared against local uploads in a single funnel.
  DRIVE_TAB_OPENED: 'drive_tab_opened',
  DRIVE_AUTH_STARTED: 'drive_auth_started',
  DRIVE_AUTH_COMPLETED: 'drive_auth_completed',
  DRIVE_AUTH_DISMISSED: 'drive_auth_dismissed',
  DRIVE_PICKER_SHOWN: 'drive_picker_shown',
  DRIVE_PICKER_CANCELLED: 'drive_picker_cancelled',
  DRIVE_PICKER_FAILED: 'drive_picker_failed',
  DRIVE_FILES_PICKED: 'drive_files_picked',
  DRIVE_FILE_METADATA_RESOLVED: 'drive_file_metadata_resolved',
  DRIVE_FILE_METADATA_FAILED: 'drive_file_metadata_failed',
  DRIVE_DISCONNECTED: 'drive_disconnected',

  // === LAYOUTS ===
  LAYOUT_SAVED: 'layout_saved',
  LAYOUT_SAVE_FAILED: 'layout_save_failed',
  LAYOUT_DELETED: 'layout_deleted',
  LAYOUT_DUPLICATED: 'layout_duplicated',

  // === APPS ===
  APP_CREATED: 'app_created',
  APP_CREATE_FAILED: 'app_create_failed',
  APP_DELETED: 'app_deleted',

  // === CONTENT ASSIGNMENT (CAST) ===
  SCREEN_CAST_FAILED: 'screen_cast_failed',
  SCREEN_CAST_PREMIUM_BLOCKED: 'screen_cast_premium_blocked',

  // === AUTH ===
  USER_SIGNED_IN: 'user_signed_in',
  USER_SIGNED_OUT: 'user_signed_out',
  USER_SIGN_IN_FAILED: 'user_sign_in_failed',

  // === CANVAS EDITOR ===
  CANVAS_OBJECT_ADDED: 'canvas_object_added',
  CANVAS_OBJECT_REMOVED: 'canvas_object_removed',

  // === CONTENT BROWSER ===
  CONTENT_BROWSER_SEARCH: 'content_browser_search',
  CONTENT_BROWSER_FILTER: 'content_browser_filter',
  CONTENT_BROWSER_SECTION_TOGGLED: 'content_browser_section_toggled',
  CONTENT_PREVIEW_CLICKED: 'content_preview_clicked',

  // === TEMPLATE GALLERY ===
  TEMPLATE_PREVIEWED: 'template_previewed',
  TEMPLATE_USED: 'template_used',
  TEMPLATE_SEARCH: 'template_search',
  TEMPLATE_FILTER_CHANGED: 'template_filter_changed',

  // === SEARCH ===
  SEARCH_PERFORMED: 'search_performed',

  // === ENTITLEMENT / LIMIT BLOCKS ===
  SCREEN_LIMIT_REACHED: 'screen_limit_reached',
  PREMIUM_FEATURE_BLOCKED: 'premium_feature_blocked',

  // === SUBSCRIPTION / BILLING ===
  // Subscription/payment webhook events are emitted from subscriptionService via safeTrack with
  // literal names ('subscription_created', 'subscription_paid', 'subscription_canceled', ...) to
  // preserve the historical event stream; they are intentionally not listed here.
  CHECKOUT_SESSION_STARTED: 'checkout_session_started',
} as const

export interface CacheableResource {
  id: string // content id for grouping
  type: 'image' | 'video'
  name?: string // content name for display in debug overlay
  urls: string[] // image: [imageUrl], video: [initUrl, seg_1, seg_2, ...]
}

/** Cache configuration sent from web-player to native layer. */
export interface CacheConf {
  /** Cache mode: off (no native cache), warm (populate only), serve (read/write cache with fallback). */
  mode: 'off' | 'warm' | 'serve'
  /** Regex patterns — serve from cache first; fall back to network on miss. */
  cacheFirstRules: string[]
  /** Regex patterns — pass through without caching (e.g. SSE streams). */
  noCacheRules: string[]
}

export const SCREEN_HEALTH_PING_INTERVAL_MS = 15_000 // 15 seconds

// A screen counts as online while its last health ping is newer than this.
// Two missed pings plus a second of slack — the single source of truth for
// online/offline on the server, the CMS and the admin panel.
export const SCREEN_ONLINE_THRESHOLD_MS = SCREEN_HEALTH_PING_INTERVAL_MS * 2 + 1000
